<template>
  <div class="app-shell">

    <!-- ✅ Show full layout only if NOT login -->
    <template v-if="!isLogin">

      <!-- SIDEBAR -->
      <aside class="sidebar" :class="{ open: sidebarOpen }">

        <!-- Brand -->
        <div class="brand">
          <div class="logo">BR</div>
          <div class="title">
            <strong>Boro Credit</strong>
            <span>Credit System</span>
          </div>
        </div>

        <!-- CORE -->
        <div class="nav-group">
          <div class="nav-label">CORE</div>

          <RouterLink
            v-if="can('dashboard')"
            to="/"
            class="nav-item"
            :class="{ active: isActive('/') }"
            @click="onNavClick"
          >
            <i class="bx bx-grid-alt"></i>
            <span>Dashboard</span>
          </RouterLink>

          <RouterLink
            v-if="can('employees')"
            to="/employees"
            class="nav-item"
            :class="{ active: isActive('/employees') }"
            @click="onNavClick"
          >
            <i class="bx bx-user"></i>
            <span>Employees</span>
          </RouterLink>

          <RouterLink
            v-if="can('users')"
            to="/users"
            class="nav-item"
            :class="{ active: isActive('/users') }"
            @click="onNavClick"
          >
            <i class="bx bx-group"></i>
            <span>Users</span>
          </RouterLink>
        </div>

        <!-- OPERATIONS -->
        <div class="nav-group">
          <div class="nav-label">OPERATIONS</div>

          <RouterLink
            v-if="can('kyc')"
            to="/kyc"
            class="nav-item"
            :class="{ active: isActive('/kyc') }"
            @click="onNavClick"
          >
            <i class="bx bx-id-card"></i>
            <span>KYC Requests</span>
          </RouterLink>

          <RouterLink
            v-if="can('credit')"
            to="/credit_requests"
            class="nav-item"
            :class="{ active: isActive('/credit_requests') }"
            @click="onNavClick"
          >
            <i class="bx bx-id-card"></i>
            <span>Credit Requests</span>
          </RouterLink>

          <RouterLink
            v-if="can('withdrawals')"
            to="/withdrawals"
            class="nav-item"
            :class="{ active: isActive('/withdrawals') }"
            @click="onNavClick"
          >
            <i class="bx bx-money-withdraw"></i>
            <span>Withdrawals</span>
          </RouterLink>

          <RouterLink
            v-if="can('repayments')"
            to="/repayments"
            class="nav-item"
            :class="{ active: isActive('/repayments') }"
            @click="onNavClick"
          >
            <i class="bx bx-receipt"></i>
            <span>Repayments</span>
          </RouterLink>
        </div>

        <!-- SETTINGS -->
        <div class="nav-group">
          <div class="nav-label">SETTINGS</div>

          <RouterLink
            v-if="can('system')"
            to="/system"
            class="nav-item"
            :class="{ active: isActive('/system') }"
            @click="onNavClick"
          >
            <i class="bx bx-cog"></i>
            <span>System Settings</span>
          </RouterLink>

          <RouterLink
            v-if="can('audit')"
            to="/audit"
            class="nav-item"
            :class="{ active: isActive('/audit') }"
            @click="onNavClick"
          >
            <i class="bx bx-shield-quarter"></i>
            <span>Audit Logs</span>
          </RouterLink>

          <button type="button" class="nav-item" @click="onLogoutClick">
            <i class="bx bx-log-out"></i>
            <span>Logout</span>
          </button>
        </div>

      </aside>

      <!-- MOBILE BACKDROP -->
      <div
        v-if="sidebarOpen"
        class="sidebar-backdrop"
        @click="sidebarOpen=false"
      ></div>

      <!-- MAIN -->
      <div class="main">

        <!-- TOPBAR -->
        <header class="topbar">
          <div class="topbar-inner">

            <button
              class="icon-btn mobile-toggle"
              @click="sidebarOpen=!sidebarOpen"
            >
              <i class="bx bx-menu"></i>
            </button>

            <div class="search"></div>

            <div class="top-actions">

              <button
                class="icon-btn"
                @click="openNotifications"
                title="Notifications"
              >
                <i class="bx bx-bell"></i>
              </button>

              <!-- USER -->
              <button class="user-chip" type="button">
                <span class="uc-avatar">
                  {{ userRole?.charAt(0) || "A" }}
                </span>

                <span class="uc-meta">
                  <strong>{{ userRole }}</strong>
                  <span>Role</span>
                </span>
              </button>

            </div>

          </div>
        </header>

        <!-- CONTENT -->
        <main>
          <RouterView />
        </main>

      </div>

    </template>

    <!-- LOGIN ONLY -->
    <template v-else>
      <main class="login-only">
        <RouterView />
      </main>
    </template>

  </div>
</template>


<script setup>
import { ref, computed,onMounted  } from "vue";
import { useRoute, useRouter } from "vue-router";
// import { useToast } from "vue-toastification";

const sidebarOpen = ref(false);
const userRole = ref("");
const route = useRoute();
const router = useRouter();
// const toast = useToast();
const menuByRole = {
  SUPER_ADMIN: [
    "dashboard","employees","users",
    "kyc","credit","withdrawals","repayments",
    "system","audit"
  ],

  ADMIN_MANAGER: [
    "dashboard","employees","users",
    "kyc","credit","withdrawals","repayments"
  ],

  KYC_OFFICER: [
    "dashboard","kyc"
  ],

  FINANCE: [
    "dashboard","withdrawals","repayments"
  ],

  SUPPORT: [
    "dashboard","users"
  ],

  AUDITOR: [
    "dashboard","audit"
  ]
};

const can = (key) => {
  return menuByRole[userRole.value]?.includes(key);
};

const isLogin = computed(() => route.path.startsWith("/login"));

const isActive = (path) => route.path === path;

const isMobile = () => window.matchMedia("(max-width: 960px)").matches;

/* ✅ Close sidebar after selecting menu option (mobile only) */
const onNavClick = () => {
  if (isMobile()) sidebarOpen.value = false;
};

const openNotifications = () => {
  window.location.href = "/noties";
};

onMounted(() => {
  const raw = sessionStorage.getItem("ldata");
  const ldata = JSON.parse(raw || "{}");
  // alert(ldata.user_role+"");
  if((ldata.user_role+"").localeCompare("admin")==0){
    userRole.value = "SUPER_ADMIN";
  }
  else{
    userRole.value = ldata.user_role || "SUPER_ADMIN";
  }
  // alert(userRole.value+"");
});

/* ✅ Logout + close sidebar (mobile only) */
const onLogoutClick = () => {
  if (isMobile()) sidebarOpen.value = false;
  sessionStorage.removeItem("ldata");
  router.replace("/login");
};
</script>

<style scoped>
/* ✅ Logout nav item (same layout + danger style) */
.nav-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 12px;
  border-radius: 16px;
  border: 1px solid transparent;
  background: transparent;
  text-decoration: none;
  cursor: pointer;

  color: rgba(15, 23, 42, 0.78);
  font-size: 13px;
  font-weight: 650;
  transition: 200ms ease;
  text-align: left;
}

.nav-item i {
  font-size: 18px;
  color: rgba(100, 116, 139, 0.92);
}

/* hover for normal items */
.nav-item:hover {
  background: rgba(241, 245, 249, 0.72);
  border-color: rgba(226, 232, 240, 0.9);
}

/* active router links */
.nav-item.active {
  background: rgba(46, 109, 231, 0.10);
  border-color: rgba(46, 109, 231, 0.16);
  color: var(--primary);
}

.nav-item.active i {
  color: var(--primary);
}

/* optional: focus accessibility */
.nav-item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.14);
}

/* ✅ MOBILE BACKDROP overlay */
.sidebar-backdrop {
  display: none;
}

/* ✅ HEADER USER CARD */
.user-chip {
  height: 44px;
  border-radius: 999px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  background: rgba(255, 255, 255, 0.9);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px 0 8px;
  cursor: pointer;
  transition: 200ms ease;
}

.user-chip:hover {
  background: rgba(241, 245, 249, 0.9);
  transform: translateY(-1px);
}

.uc-avatar {
  height: 34px;
  width: 34px;
  border-radius: 999px;
  background: var(--primary-10);
  display: grid;
  place-items: center;
  color: var(--primary);
  font-weight: 900;
}

.uc-meta {
  display: grid;
  line-height: 1.1;
  text-align: left;
}

.uc-meta strong {
  font-size: 12px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.92);
}

.uc-meta span {
  font-size: 11px;
  font-weight: 700;
  color: rgba(100, 116, 139, 0.9);
}

.user-chip i {
  font-size: 18px;
  color: rgba(100, 116, 139, 0.9);
}

@media (max-width: 960px) {
  .sidebar-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(2, 6, 23, 0.55);
    backdrop-filter: blur(8px);
    z-index: 88; /* below sidebar */
  }

  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 89; /* above backdrop */
    transform: translateX(-105%);
    transition: transform 250ms ease;
  }

  .sidebar.open {
    transform: translateX(0);
  }

  /* ✅ optional: hide role text in mobile to keep header clean */
  .uc-meta span {
    display: none;
  }
}

@media (max-width: 520px) {
  /* ✅ on very small screens show only avatar */
  .uc-meta {
    display: none;
  }
  .user-chip {
    padding-right: 8px;
  }
}
</style>
